import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/contexts/AuthContext';
import {
  LayoutDashboard, BarChart3, Users, ShieldCheck, Layers,
  ArrowDownCircle, ArrowUpCircle, TrendingUp, Receipt,
  Settings, Gift, Briefcase, Calendar,
  CreditCard, FileBarChart, Bell, MessageSquare,
  ClipboardList, UserCheck, Home, LogOut, Menu, X, ChevronDown, ChevronRight,
  Search, Zap, Trophy, Award, Lock
} from 'lucide-react';

interface SidebarGroup {
  label: string;
  items: { path: string; label: string; icon: any }[];
}

const sidebarGroups: SidebarGroup[] = [
  {
    label: 'Overview',
    items: [
      { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Users',
    items: [
      { path: '/admin/users', label: 'All Users', icon: Users },
      { path: '/admin/kyc', label: 'KYC', icon: ShieldCheck },
      { path: '/admin/team', label: 'Team / MLM', icon: Layers },
    ],
  },
  {
    label: 'Finance',
    items: [
      { path: '/admin/deposits', label: 'Deposits', icon: ArrowDownCircle },
      { path: '/admin/withdrawals', label: 'Withdrawals', icon: ArrowUpCircle },
      { path: '/admin/transactions', label: 'Transactions', icon: Receipt },
    ],
  },
  {
    label: 'Income',
    items: [
      { path: '/admin/bonuses', label: 'Bonuses', icon: Gift },
      { path: '/admin/bonanza-ranks', label: 'Bonanza Ranks', icon: Trophy },
      { path: '/admin/awards', label: 'Awards & Rewards', icon: Award },
      { path: '/admin/level-unlock', label: 'Level Unlock Rules', icon: Lock },
    ],
  },
  {
    label: 'Investments',
    items: [
      { path: '/admin/plans', label: 'Plans', icon: Briefcase },
      { path: '/admin/investments', label: 'Investments', icon: Zap },
      { path: '/admin/working-days', label: 'Working Days', icon: Calendar },
    ],
  },
  {
    label: 'Payments',
    items: [
      { path: '/admin/payment-methods', label: 'Payment Methods', icon: CreditCard },
    ],
  },
  {
    label: 'Reports',
    items: [
      { path: '/admin/reports', label: 'Reports', icon: FileBarChart },
    ],
  },
  {
    label: 'Communication',
    items: [
      { path: '/admin/notifications', label: 'Notifications', icon: Bell },
      { path: '/admin/support', label: 'Support', icon: MessageSquare },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/admin/audit-logs', label: 'Audit Logs', icon: ClipboardList },
      { path: '/admin/business-rules', label: 'Business Rules', icon: Settings },
      { path: '/admin/settings', label: 'Settings', icon: Settings },
      { path: '/admin/admins', label: 'Admin Users', icon: UserCheck },
    ],
  },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(sidebarGroups.map(g => [g.label, true]))
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-fintech-900 text-white overflow-hidden">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-fintech-950 border-r border-white/[0.06] transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-white/[0.06] shrink-0 ${collapsed ? 'justify-center px-2' : 'px-4 justify-between'}`}>
          {!collapsed && (
            <Link to="/admin" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
                <span className="text-white font-bold text-xs">GR</span>
              </div>
              <div>
                <span className="font-bold text-sm text-white tracking-tight block leading-tight">GR Trading</span>
                <span className="text-2xs text-primary-400 font-medium">Admin Panel</span>
              </div>
            </Link>
          )}
          {collapsed && (
            <Link to="/admin" className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
              <span className="text-white font-bold text-xs">GR</span>
            </Link>
          )}
          <button onClick={() => collapsed ? setCollapsed(false) : setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-none">
          {sidebarGroups.map(group => (
            <div key={group.label} className="mb-1">
              {!collapsed && (
                <button onClick={() => toggleGroup(group.label)}
                  className="d-nav-group flex items-center justify-between w-full hover:text-slate-400 transition-colors">
                  {group.label}
                  {expandedGroups[group.label] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
              )}
              {(collapsed || expandedGroups[group.label]) && group.items.map(item => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                    className={`${active ? 'd-nav-item-active' : 'd-nav-item-default'} ${collapsed ? 'justify-center px-2' : ''}`}
                    title={collapsed ? item.label : undefined}>
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Collapse toggle */}
        <div className="hidden lg:flex items-center justify-center py-2 border-t border-white/[0.06]">
          <button onClick={() => setCollapsed(!collapsed)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-300 hover:bg-white/[0.04] transition-colors">
            <ChevronRight className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* User */}
        {!collapsed && (
          <div className="p-3 border-t border-white/[0.06] shrink-0">
            <div className="flex items-center gap-3 px-2 py-1.5">
              <div className="w-8 h-8 rounded-full bg-primary-600/20 border border-primary-500/30 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-400">{user?.username?.[0]?.toUpperCase() || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                <p className="text-xs text-slate-500">Administrator</p>
              </div>
              <button onClick={handleLogout} className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg hover:bg-white/[0.04] transition-colors" title="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 flex items-center justify-between px-4 md:px-6 border-b border-white/[0.06] bg-fintech-900/80 backdrop-blur-xl shrink-0 sticky top-0 z-30">
          <button className="lg:hidden p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04]" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>

          <div className="relative hidden md:block flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search..." className="d-input h-9 pl-9 pr-4 text-sm w-full" />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Link to="/admin/notifications" className="relative p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04] transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </Link>
            <div className="w-px h-6 bg-white/[0.06] mx-1 hidden sm:block" />
            <Link to="/dashboard" className="d-btn d-btn-sm d-btn-secondary text-xs hidden sm:inline-flex">
              View User Panel
            </Link>
            <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors cursor-default">
              <div className="w-8 h-8 rounded-full bg-primary-600/20 border border-primary-500/30 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-400">{user?.username?.[0]?.toUpperCase() || 'A'}</span>
              </div>
              <span className="text-sm font-medium text-slate-200 hidden sm:block">{user?.username}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
